import { ApolloError } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex, localStorage, Spinner, Text, ViewerContext } from "dash-ui";
import { RoutesPath } from "flow-components/routes/routes";
import {
  useGetFullAccountQuery,
  useTemporaryAuthenticationMutation,
} from "gql/generated.graphql";
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { AuthPagesLayout } from "v2/components/auth-pages-layout";
import { z } from "zod";

const signInFormSchema = z.object({
  email: z.string().min(1, "Email is required.").email("Email must be valid."),
  password: z.string().min(1, "Password is required."),
});

type SignInFormProps = z.infer<typeof signInFormSchema>;

export function SignInPage() {
  // React states
  const [error, setError] = useState("");

  // Query and Mutations
  const getAccountQuery = useGetFullAccountQuery();
  const [tempAuthMutation] = useTemporaryAuthenticationMutation();

  // Hooks
  const navigate = useNavigate();
  const auth = useContext(ViewerContext);

  // Constants
  const signInForm = useForm<SignInFormProps>({
    resolver: zodResolver(signInFormSchema),
  });

  // Handle sign in functionality
  async function signIn(data: SignInFormProps) {
    try {
      const result = await tempAuthMutation({
        variables: { email: data.email, password: data.password },
      });

      const tempToken = result.data?.temporaryAuthentication.tempToken;
      if (!tempToken) throw new Error("Token was not provided by API.");
      localStorage.setAccessToken(tempToken);

      navigate("/auth/sign-in/mfa");
    } catch (error) {
      if ((error as ApolloError).message === "INCORRECT_ACCOUNT_CREDENTIALS") {
        setError("Incorrect credentials.");
        return;
      }
      setError((error as Error).message);
    }
  }

  // Check if we have tokens in context (indicates auth is being processed)
  const contextHasTokens =
    auth.tokens?.accessToken && auth.tokens?.refreshToken;

  // Redirect if user already logged in
  useEffect(() => {
    // If user has account in context, redirect to home
    if (auth.account) {
      navigate(RoutesPath.Properties);
      return;
    }

    // Only check localStorage if context has no tokens
    const accessToken = localStorage.getAccessToken();
    const refreshToken = localStorage.getRefreshToken();
    if (accessToken && !refreshToken) return navigate(RoutesPath.SignInMFA);
    if (accessToken && refreshToken) return navigate(RoutesPath.Properties);
  }, [auth.account, auth.tokens]);

  // Show loading state when we have tokens but no account yet
  if (contextHasTokens && !auth?.account) {
    return (
      <AuthPagesLayout>
        <Flex
          justifyContent="center"
          alignItems="center"
          h="100%"
          flexDirection="column"
        >
          <Text lg>Loading account info, please wait...</Text>
          <Spinner mt={4} />
          <Flex mt={8} w={280}>
            <Text>
              <i>
                If you are not redirected in 5 seconds, please refresh the page
                or contact support.
              </i>
            </Text>
          </Flex>
        </Flex>
      </AuthPagesLayout>
    );
  }

  // Return HTML element
  return (
    <AuthPagesLayout>
      <div className="flex flex-col items-center justify-center h-full w-full">
        <form
          className="flex flex-col w-full max-w-80"
          onSubmit={signInForm.handleSubmit(signIn)}
        >
          <h1 className="text-3xl font-[montserrat] font-bold text-slate-800 mt-1">
            Welcome to DASH!
          </h1>
          <p className="text-slate-500">Sign in to your account.</p>
          <label className="text-sm text-slate-500 mt-6">Email</label>
          <input
            type="email"
            placeholder="john@dash.com"
            className={`h-10 border ${
              signInForm.formState.errors.email
                ? "border-red-300 bg-red-50"
                : "border-slate-300"
            } rounded px-4`}
            {...signInForm.register("email")}
          />
          <span className="text-sm text-red-600">
            {signInForm.formState.errors.email?.message}
          </span>
          <label className="text-sm text-slate-500 mt-2">Password</label>
          <input
            type="password"
            placeholder="Enter password"
            className={`h-10 border ${
              signInForm.formState.errors.password
                ? "border-red-300 bg-red-50"
                : "border-slate-300"
            } rounded px-4`}
            {...signInForm.register("password")}
          />
          <span className="text-sm text-red-600">
            {signInForm.formState.errors.password?.message}
          </span>
          {error && (
            <span className="border border-red-300 bg-red-50 h-10 px-4 rounded flex items-center text-sm text-red-600 mt-4">
              ⚠️ {error}
            </span>
          )}
          <button
            type="submit"
            className="h-10 bg-sky-600 text-white rounded mt-4"
          >
            Login
          </button>
          <a className="mt-8">Forgot your password?</a>
        </form>
      </div>
    </AuthPagesLayout>
  );
}
